import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useScrollToTop } from '@/hooks/useScrollToTop';
import { trpc } from '@/lib/trpc';

import OrderDetailHeader from '../features/order-detail/components/OrderDetailHeader';
import OrderItemList from '../features/order-detail/components/OrderItemList';
import OrderStatusBadge from '../features/order-detail/components/OrderStatusBadge';
import PickupInfo from '../features/order-detail/components/PickupInfo';

const isValidToken = (token: string | undefined): token is string => {
  return !!token && token.length === 12;
};

export default function OrderDetailPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const { token } = useParams();
  const hasValidToken = isValidToken(token);

  const {
    data: orderDetail,
    isLoading,
    isError,
    error,
  } = useQuery(
    trpc.order.getByToken.queryOptions(
      { token: token ?? '' },
      {
        enabled: hasValidToken,
        retry: false,
      }
    )
  );

  if (!hasValidToken) {
    return (
      <div className="min-h-screen bg-white w-full">
        <OrderDetailHeader />
        <div className="h-[50px]" />
        <div className="flex items-center justify-center min-h-[calc(100vh-50px)] px-5">
          <div className="text-center space-y-4">
            <p className="text-red-600">유효하지 않은 주문 링크입니다.</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-150"
            >
              홈으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white w-full">
        <OrderDetailHeader />
        <main className="flex items-center justify-center min-h-[calc(100vh-50px)] animate-in fade-in duration-300">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </main>
      </div>
    );
  }

  if (isError || !orderDetail) {
    return (
      <div className="min-h-screen bg-white w-full">
        <OrderDetailHeader />
        <div className="h-[50px]" />
        <div className="flex items-center justify-center min-h-[calc(100vh-50px)] px-5">
          <div className="text-center space-y-4">
            <p className="text-red-600">{error?.message || '주문 정보를 찾을 수 없습니다.'}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-150"
            >
              홈으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <OrderDetailHeader />

      <main className="flex-grow animate-in fade-in duration-300">
        <section className="px-5 py-8 space-y-6">
          <div className="bg-[#f0f6ff] text-[#1F1F1F] rounded-lg py-6 px-4">
            <div className="flex items-center justify-center gap-2">
              <p className="text-md font-semibold text-[#454545]">주문번호</p>
              <p className="text-md font-semibold text-[#3D8AFF]">{orderDetail.orderNumber}</p>
              <OrderStatusBadge status={orderDetail.paymentStatus} />
            </div>
          </div>

          <PickupInfo customerName={orderDetail.customerName} pickupDatetime={orderDetail.pickupDatetime} />
        </section>

        <div className="h-1.5 bg-gray-100" />

        <section className="px-5 py-8">
          <OrderItemList items={orderDetail.items} totalAmount={orderDetail.totalAmount} />
        </section>
      </main>
    </div>
  );
}
